import { doc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  addNewMessage,
  createConversationThread,
  getDb,
  getLastMessage,
  getMessages,
} from "../../services/FirebaseStorage";
import {
  AuthService,
  EventService,
  NotificationService,
  RatingReviewService,
} from "../../services";
import { toast } from "react-toastify";
import { formatDateENGB } from "../../utils/common-methods";
import { injectModels } from "../../redux/injectModels";
import { Form } from "react-bootstrap";
import { globalConstant } from "../../constants/globalConstant";
import { UserRoleType } from "../../constants/Enum";

const GroupMessageBox = () => {
  const location = useLocation();
  let loginUser = AuthService.getLoginUser();
  const [messages, setMessages] = useState([]);
  var [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const [isLoading, setLoading] = useState(false);

  const thread = location.state?.thread || null;
  const threadId = thread?.id || null;
  const isThreadExist = location.state?.isThreadExist || false;

  var todayDate = new Date();

  todayDate = todayDate.setHours(0, 0, 0, 0);

  useEffect(() => {
    setLoading(true);
  }, [threadId]);

  const addDefaultImg = (e, isGroup = false) => {
    if (e && e.target) {
      e.target.onerror = null;
      e.target.src = isGroup
        ? "/assets/images/users/multi-user.jpg"
        : "/assets/images/users/user-dummy-img.jpg";
    }
  };

  //handle message listener
  useEffect(() => {
    let unsubscribe = null;

    const bootstrapAsync = async () => {
      try {
        if (!threadId) {
          console.warn("No thread ID available");
          setLoading(false);
          return;
        }

        if (!loginUser?.id) {
          console.warn("No login user found");
          setLoading(false);
          return;
        }

        await getEventProfiles();

        const messagesData = await getLastMessage(
          threadId,
          "swallow_group_chat",
        );

        if (messagesData?.length > 0 && thread?.lastRead && loginUser?.id) {
          const db = await getDb();
          if (!db) {
            console.error("Database connection failed");
            setLoading(false);
            return;
          }

          const lastMessage = messagesData[0];
          const currentUserLastRead = thread?.lastRead?.[loginUser?.id];

          if (
            lastMessage?.userId !== loginUser?.id &&
            lastMessage?.created !== currentUserLastRead
          ) {
            // update thread
            let lastRead = { ...(thread?.lastRead || {}) };

            const threadRef = await doc(
              db,
              `swallow_group_chat`,
              `${threadId}`,
            );

            if (threadRef) {
              lastRead[loginUser?.id] = lastMessage?.created || new Date();

              let usersInfo = { ...(thread?.usersInfo || {}) };

              try {
                const res = await RatingReviewService.getBusinessProfileById(
                  loginUser?.id,
                );

                const updatedLogo = res?.data?.businessLogoUrl || "";

                if (usersInfo[loginUser?.id]) {
                  usersInfo[loginUser?.id] = {
                    name: usersInfo[loginUser?.id]?.name || "User",
                    avatar: updatedLogo || "",
                  };
                }

                await updateDoc(threadRef, {
                  lastRead: lastRead,
                  usersInfo: usersInfo,
                });
              } catch (e) {
                console.error("Error updating thread document:", e);
              }
            }
          }
        }

        unsubscribe = await getMessages(
          threadId,
          async (messageData) => {
            setLoading(false);
            if (messageData && Array.isArray(messageData)) {
              addDateSpearationForMessages(messageData);
            }
          },
          "swallow_group_chat",
        );
      } catch (e) {
        console.error("ERROR_boot: ", e);
        setLoading(false);
      }
    };

    if (threadId && loginUser?.id) {
      bootstrapAsync();
    } else {
      setLoading(false);
    }

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [threadId]);

  const handleMessageChange = (e) => {
    e.preventDefault();
    if (!e || !e.target) return;

    const val = e.target.value;
    if (val?.length > 0) {
      setMessageError("");
    }
    setMessage(val || "");
  };

  const addDateSpearationForMessages = (messageData) => {
    if (
      !messageData ||
      !Array.isArray(messageData) ||
      messageData.length === 0
    ) {
      setMessages([]);
      return;
    }

    if (!loginUser?.id || !thread?.users) {
      setMessages([]);
      return;
    }

    var lastCompareDate = null;
    var newMessages = [];

    const filteredMessages =
      loginUser?.role === UserRoleType.EO
        ? messageData
        : messageData.filter(
            (message) =>
              message?.userId === loginUser?.id ||
              message?.userId === thread?.users[0],
          );

    filteredMessages.forEach((message) => {
      if (!message || !message?.created) return;

      try {
        var newDate = message?.created?.toDate();
        if (!newDate) return;

        newDate = newDate.setHours(0, 0, 0, 0);

        if (lastCompareDate == null) {
          lastCompareDate = newDate;
          newMessages.push({
            message: `${formatDateENGB(message?.created?.toDate())}`,
            type: "divider",
            date: lastCompareDate,
          });
        }

        if (lastCompareDate !== newDate) {
          newMessages.push({
            message: `${formatDateENGB(message?.created?.toDate())}`,
            type: "divider",
            date: newDate,
          });
        }

        newMessages.push(message);
        lastCompareDate = newDate;
      } catch (error) {
        console.error("Error processing message date:", error);
      }
    });

    setMessages(newMessages);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!loginUser?.id) {
       toast.error("Session expired. Please login again.");
      return;
    }

    if (!threadId) {
      toast.error("Invalid conversation thread");
      return;
    }

    if (!message || message.trim() === "") {
      setMessageError("Please enter some text");
      return;
    }

    if (
      globalConstant.emailRegex?.test(message) ||
      globalConstant.phoneRegex?.test(message)
    ) {
      setMessageError("Phone numbers & email are not allowed");
      return;
    } else {
      setMessageError("");
    }

    const lastRead = {};
    const currentTime = new Date();
    const messageObj = {
      message: message.trim(),
      created: currentTime,
      userId: loginUser?.id,
    };

    setMessage("");

    try {
      if (!isThreadExist) {
        if (!thread?.users || !Array.isArray(thread.users)) {
          toast.error("Invalid thread configuration");
          return;
        }

        thread.users.forEach((id) => {
          if (id === loginUser?.id) {
            lastRead[id] = currentTime;
          } else {
            lastRead[id] = new Date(currentTime.getTime() - 1000 * 60);
          }
        });

        let threadObj = { ...thread };
        threadObj.created = currentTime;
        threadObj.lastRead = lastRead;

        await createConversationThread(
          threadId,
          threadObj,
          "swallow_group_chat",
        );
      } else {
        // update read timestamp for users
        if (thread?.users && Array.isArray(thread.users)) {
          thread.users.forEach((id) => {
            if (id === loginUser?.id) {
              lastRead[id] = currentTime;
            } else {
              lastRead[id] =
                thread?.lastRead?.[id] ||
                new Date(currentTime.getTime() - 1000 * 60);
            }
          });
        }

        await updateThread(lastRead);
      }

      var newMessages = [...messages];
      newMessages.push(messageObj);
      setMessages(newMessages);

      const docRef = await addNewMessage(
        threadId,
        messageObj,
        "swallow_group_chat",
      );

      if (!docRef || docRef.id == null) {
        toast.error("Error while sending message");
      } else {
        if (thread?.users?.length > 0) {
          // If EO sends a message then send notification to all traders
          // if trader sends a message only send notification to EO
          const usersFilter =
            loginUser?.role === UserRoleType.EO
              ? thread?.users?.filter((user) => user !== loginUser?.id)
              : thread?.users?.[0]
              ? [thread.users[0]]
              : [];

          if (usersFilter && usersFilter.length > 0) {
            sendEmailNotification(
              usersFilter,
              message.trim(),
              thread?.title || "Group Message",
              true,
            );
          }
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    }
  };

  const updateThread = async (lastRead) => {
    if (!lastRead) {
      console.error("Missing lastRead parameter");
      return;
    }

    if (!threadId) {
      console.error("Thread ID is missing");
      return;
    }

    try {
      const db = await getDb();
      if (!db) {
        console.error("Database connection failed");
        return;
      }

      const threadRef = await doc(db, `swallow_group_chat`, `${threadId}`);
      if (threadRef) {
        await updateDoc(threadRef, {
          lastRead: lastRead,
        });
      }
    } catch (error) {
      console.error("Error updating thread:", error);
    }
  };

  const sendEmailNotification = async (users, message, title, isGroup) => {
    if (!users || !users.length || !message || !title) {
      console.warn("Missing parameters for email notification");
      return;
    }

    try {
      const params = {
        to: users,
        message: message,
        title: title,
        isGroup: isGroup || false,
      };
      await NotificationService.sendEmailNotification(params);
    } catch (error) {
      console.error("Error sending email notification:", error);
    }
  };

  const getAvatar = (message = null) => {
    if (!thread) return "";

    if (message && message?.userId) {
      return thread?.usersInfo?.[message?.userId]?.avatar || "";
    } else {
      return thread?.avatar || "";
    }
  };

  const updateAvatarThread = async (avatarPath) => {
    if (!threadId) {
      console.error("Thread ID is missing");
      return;
    }

    try {
      const db = await getDb();
      if (!db) {
        console.error("Database connection failed");
        return;
      }

      const threadRef = await doc(db, `swallow_group_chat`, `${threadId}`);
      if (threadRef) {
        await updateDoc(threadRef, {
          avatar: avatarPath || "",
        });
      }
    } catch (error) {
      console.error("Error updating avatar:", error);
    }
  };

  const getEventProfiles = async () => {
    if (!threadId) {
      console.warn("No thread ID for event profiles");
      return null;
    }

    try {
      const res = await EventService.GetApprovedTradersByEventId(threadId);

      if (res?.isSuccess && res?.data) {
        await updateAvatarThread(res?.data?.eventAvatar || "");
        return res?.data;
      } else {
        await updateAvatarThread("");
      }
      return null;
    } catch (e) {
      console.error("Error fetching event profiles:", e);
      return null;
    }
  };

  const getUserName = (message) => {
    if (!message || !message?.userId || !thread?.usersInfo) {
      return "User";
    }
    return thread?.usersInfo[message?.userId]?.name || "User";
  };

  // Early return if no thread exists
  if (!threadId && !isLoading) {
    return (
      <div className="text-center p-4">
        <p>No conversation selected</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="modal-dialog-scrollable">
        <div className="modal-content">
          <div className="msg-head">
            <div className="row">
              <div className="col-12">
                <div className="d-flex align-items-center">
                  <span className="chat-icon">
                    <img
                      className="img-fluid"
                      src="https://mehedihtml.com/chatbox/assets/img/arroleftt.svg"
                      alt="back_image"
                    />
                  </span>
                  <div className="flex-shrink-0">
                    <img
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: 25,
                        border: "2px solid gray",
                      }}
                      className="img-fluid"
                      src={getAvatar()}
                      alt="user img"
                      onError={(e) => {
                        addDefaultImg(e, true);
                      }}
                    />
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h3>{thread?.title || "Group Chat"}</h3>
                    {loginUser?.role === UserRoleType.EO && (
                      <h5 className="text-dark-emphasis">
                        Broadcast your messages to all of your individual event
                        attendees in one go and only receive private messages in
                        return.
                      </h5>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="messageContainerScroll" className="modal-body bottom-scroll">
            <div className="bottom-scroll">
              {isLoading ? (
                <h4 style={{ textAlign: "center" }}>Loading...</h4>
              ) : null}

              {messages?.length > 0 && !isLoading ? (
                <div className="msg-body">
                  <ul id="messageContainer">
                    {messages.map((message, index) => (
                      <React.Fragment key={index}>
                        {message?.type === "divider" ? (
                          <li>
                            <div className="divider">
                              <h6>
                                {todayDate === message?.date
                                  ? "Today"
                                  : message?.message || ""}
                              </h6>
                            </div>
                          </li>
                        ) : (
                          <li
                            className={
                              message?.userId === loginUser?.id
                                ? "repaly"
                                : "sender no-before"
                            }
                          >
                            {message?.userId === loginUser?.id ? (
                              <>
                                <p>{message?.message || ""}</p>
                                <span className="time">
                                  {message?.timeString || ""}
                                </span>
                              </>
                            ) : (
                              <div style={{ display: "flex", columnGap: 10 }}>
                                <div>
                                  <img
                                    style={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 20,
                                      border: "2px solid gray",
                                      position: "relative",
                                    }}
                                    className="img-fluid"
                                    src={getAvatar(message)}
                                    alt="user img"
                                    onError={(e) => addDefaultImg(e, false)}
                                  />
                                </div>
                                <div>
                                  <span className="time">
                                    {getUserName(message)}
                                  </span>
                                  <p>{message?.message || ""}</p>
                                  <span className="time">
                                    {message?.timeString || ""}
                                  </span>
                                </div>
                              </div>
                            )}
                          </li>
                        )}
                      </React.Fragment>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          <div className="send-box">
            <form onSubmit={onSubmit}>
              <Form.Control
                className="form-control"
                aria-label="message…"
                placeholder="Write message…"
                as="textarea"
                rows={1}
                style={{ maxHeight: 100 }}
                value={message}
                onChange={handleMessageChange}
              />
              <button type="button" onClick={onSubmit}>
                <i className="fa fa-paper-plane" aria-hidden="true"></i> Send
              </button>
            </form>
            {messageError && <p className="text-danger">{messageError}</p>}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default injectModels(["application"])(GroupMessageBox);
